import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { supabase, getAccessToken } from '../lib/supabase';
import { API } from '../config/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('Processing authentication...');
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      try {
        // Check for callback type (signup confirmation, recovery, etc.)
        const callbackType = searchParams.get('type');
        const hash = window.location.hash;
        
        // Handle email confirmation callback
        if (callbackType === 'signup' || callbackType === 'email_confirmation') {
          console.log('[AuthCallback] Processing email confirmation callback');
          setStatus('Confirming your email...');
          
          // Supabase handles the confirmation via the URL token
          // After confirmation, Supabase creates a session
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('[AuthCallback] Email confirmation error:', error);
            toast.error('Email confirmation failed. Please try again or contact support.');
            navigate('/login');
            return;
          }
          
          if (session) {
            console.log('[AuthCallback] Email confirmed! Session established for:', session.user?.email);
            toast.success('Email confirmed! Welcome to Squidemy! 🦑');
            
            // Verify with backend
            setStatus('Setting up your account...');
            try {
              await axios.get(`${API}/auth/init`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                withCredentials: true,
                timeout: 15000
              });
            } catch (err) {
              console.warn('[AuthCallback] Backend init after confirmation:', err.message);
            }
            
            await refreshUser();
            navigate('/onboarding', { replace: true });
            return;
          }
          
          // No session after confirmation - user needs to log in
          toast.success('Email confirmed! Please log in to continue.');
          navigate('/login', { replace: true });
          return;
        }
        
        // Handle password recovery callback
        if (callbackType === 'recovery') {
          console.log('[AuthCallback] Processing password recovery callback');
          setStatus('Preparing password reset...');
          
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('[AuthCallback] Recovery error:', error);
            toast.error('Password reset link expired. Please request a new one.');
            navigate('/login');
            return;
          }
          
          if (session) {
            toast.info('Please set your new password.');
            navigate('/reset-password', { replace: true, state: { fromRecovery: true } });
            return;
          }
          
          toast.error('Password reset link expired. Please request a new one.');
          navigate('/login');
          return;
        }
        
        // Check for Supabase OAuth callback (access_token in hash)
        if (hash && hash.includes('access_token')) {
          console.log('[AuthCallback] Processing Supabase OAuth callback');
          setStatus('Establishing session...');
          
          // Supabase automatically handles the hash and sets the session
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('[AuthCallback] Supabase session error:', error);
            toast.error('Authentication failed: ' + error.message);
            navigate('/login');
            return;
          }
          
          if (session) {
            console.log('[AuthCallback] Supabase session established for:', session.user?.email);
            setStatus('Verifying with server...');
            
            // CRITICAL: Verify the session works with our backend BEFORE redirecting
            // This ensures the user is properly created/linked in MongoDB
            const token = session.access_token;
            let retries = 3;
            let backendSuccess = false;
            
            while (retries > 0 && !backendSuccess) {
              try {
                console.log(`[AuthCallback] Verifying backend auth (attempt ${4 - retries}/3)...`);
                const response = await axios.get(`${API}/auth/init`, {
                  headers: { Authorization: `Bearer ${token}` },
                  withCredentials: true,
                  timeout: 10000
                });
                
                if (response.data?.user) {
                  backendSuccess = true;
                  console.log('[AuthCallback] Backend verified user:', response.data.user.email);
                  
                  // Determine redirect based on role
                  const provider = session.user?.app_metadata?.provider || 'oauth';
                  toast.success(`Successfully signed in with ${provider.charAt(0).toUpperCase() + provider.slice(1)}!`);
                  
                  const isAdmin = response.data.user.role === 'admin' || response.data.user.role === 'super_admin';
                  
                  // Use navigate instead of window.location.replace to preserve React state
                  if (isAdmin) {
                    navigate('/admin', { replace: true });
                  } else {
                    navigate('/blackboard', { replace: true });
                  }
                  return;
                }
              } catch (err) {
                console.warn(`[AuthCallback] Backend verification attempt failed:`, err.message);
                retries--;
                if (retries > 0) {
                  setStatus(`Retrying authentication (${3 - retries}/3)...`);
                  await new Promise(resolve => setTimeout(resolve, 1500));
                }
              }
            }
            
            // If backend verification failed after all retries, still try to proceed
            if (!backendSuccess) {
              console.warn('[AuthCallback] Backend verification failed, proceeding anyway...');
              toast.success('Signed in! Loading your profile...');
              navigate('/blackboard', { replace: true });
            }
            return;
          }
        }
        
        // Check for token in query params (legacy Google OAuth flow)
        const token = searchParams.get('token');
        
        if (token) {
          console.log('[AuthCallback] Processing legacy token from OAuth');
          localStorage.setItem('auth_token', token);
          
          await refreshUser();
          
          try {
            const userResponse = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true
            });
            
            const user = userResponse.data;
            toast.success('Successfully signed in!');
            
            if (user?.role === 'admin' || user?.role === 'super_admin') {
              navigate('/admin', { replace: true });
            } else {
              navigate('/blackboard', { replace: true });
            }
          } catch (userError) {
            console.log('[AuthCallback] Could not fetch user, redirecting to dashboard');
            toast.success('Successfully signed in!');
            navigate('/blackboard', { replace: true });
          }
          return;
        }
        
        // Check for error in query params
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        if (error) {
          console.error('[AuthCallback] OAuth error:', error, errorDescription);
          toast.error(errorDescription || 'Authentication failed');
          navigate('/login');
          return;
        }
        
        // Legacy flow: Check for session_id in hash (Emergent auth)
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');
        
        if (sessionId) {
          console.log('[AuthCallback] Processing legacy session_id');
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
            withCredentials: true
          });
          
          if (response.data.session_token) {
            localStorage.setItem('auth_token', response.data.session_token);
          }
          
          await refreshUser();
          
          const isNewUser = response.data.is_new_user;
          const needsSquidTour = response.data.needs_squidtour;
          
          if (isNewUser) {
            toast.success('Welcome to Squidemy! Let\'s start your orientation. 🦑');
            navigate('/worlds/squidtour', { replace: true });
          } else if (needsSquidTour) {
            toast.info('Please complete the SquidTour orientation to continue.');
            navigate('/worlds/squidtour', { replace: true });
          } else {
            toast.success('Successfully signed in!');
            navigate('/blackboard', { replace: true });
          }
          return;
        }
        
        // No valid auth data found - might be Supabase redirect without hash yet
        // Wait a moment for Supabase to process
        setStatus('Waiting for authentication response...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const { data: { session: delayedSession } } = await supabase.auth.getSession();
        if (delayedSession) {
          console.log('[AuthCallback] Delayed Supabase session found for:', delayedSession.user?.email);
          
          // Verify with backend
          setStatus('Verifying with server...');
          const token = delayedSession.access_token;
          
          try {
            const response = await axios.get(`${API}/auth/init`, {
              headers: { Authorization: `Bearer ${token}` },
              withCredentials: true,
              timeout: 10000
            });
            
            if (response.data?.user) {
              toast.success('Successfully signed in!');
              navigate('/blackboard', { replace: true });
              return;
            }
          } catch (err) {
            console.warn('[AuthCallback] Backend verification failed:', err.message);
          }
          
          // Fallback - try anyway
          toast.success('Signed in! Loading your profile...');
          navigate('/blackboard', { replace: true });
          return;
        }
        
        console.error('[AuthCallback] No valid authentication data found');
        toast.error('Authentication failed: No credentials found');
        navigate('/login');
        
      } catch (error) {
        console.error('[AuthCallback] Error:', error);
        toast.error(error.response?.data?.detail || 'Authentication failed');
        navigate('/login');
      }
    };
    
    processAuth();
  }, [navigate, refreshUser, searchParams]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-background">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-lg font-heading text-foreground">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
